/**
 * One support ticket, with its conversation.
 *
 * Internal notes do not appear here, and not because this component filters them: the
 * server's query for a customer excludes them, so they are never in the payload. That
 * is deliberate — a filter in a view is one refactor away from being forgotten, and
 * the thing being protected is a note we wrote about somebody, to them.
 */

import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useAccount, useAddTicketComment, useSetTicketStatus, useTicket } from '@/shared/api/hooks'
import type { PreparedAttachment } from '@/shared/api/hooks'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import { AttachmentPicker, AttachmentStrip } from '@/shared/ui/AttachmentPicker'
import {
  Badge,
  Button,
  ErrorNotice,
  Field,
  Loading,
  Notice,
  Panel,
  formatDateTime,
  formatRelative,
} from '@/shared/ui/primitives'
import { TicketStatusBadge } from './Support'

const MAX_COMMENT = 4000
const MAX_IMAGES = 5

export function SupportTicket() {
  const { id } = useParams<{ id: string }>()
  const { data: account } = useAccount()
  const { data: ticket, isPending, error } = useTicket(id)
  const comment = useAddTicketComment(id ?? '')
  const setStatus = useSetTicketStatus(id ?? '')

  const [body, setBody] = useState('')
  const [attachments, setAttachments] = useState<PreparedAttachment[]>([])

  const attachmentsEnabled = account?.support_attachments_enabled ?? false
  const maxBytes = 2 * 1024 * 1024

  useEffect(() => {
    return () => {
      for (const attachment of attachments) URL.revokeObjectURL(attachment.previewUrl)
    }
    // Unmount-only: see the same note in NewTicket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function onReply(event: React.FormEvent) {
    event.preventDefault()
    if (body.trim().length === 0) return
    comment.mutate(
      { body, attachments },
      {
        onSuccess: () => {
          setBody('')
          for (const attachment of attachments) URL.revokeObjectURL(attachment.previewUrl)
          setAttachments([])
        },
      },
    )
  }

  if (isPending) {
    return (
      <AppShell>
        <Loading label="Loading the ticket" />
      </AppShell>
    )
  }

  if (error || !ticket) {
    return (
      <AppShell>
        <PageHead index="05" eyebrow="Support" title="Ticket" />
        <ErrorNotice error={error} />
        <Link to="/support">
          <Button>Back to support</Button>
        </Link>
      </AppShell>
    )
  }

  const closed = ticket.status === 'closed'

  return (
    <AppShell>
      <PageHead
        index="05"
        eyebrow="Support"
        title={ticket.title}
        subtitle={`Opened ${formatDateTime(ticket.created_at)} · ${ticket.id}`}
        actions={
          <div className="row" style={{ gap: 'var(--s2)' }}>
            <Link to="/support">
              <Button variant="ghost">All tickets</Button>
            </Link>
            <Button
              loading={setStatus.isPending}
              onClick={() => setStatus.mutate(closed ? 'open' : 'closed')}
            >
              {closed ? 'Reopen' : 'Close ticket'}
            </Button>
          </div>
        }
      />

      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        <div className="row" style={{ gap: 'var(--s3)' }}>
          <TicketStatusBadge status={ticket.status} />
          {closed && ticket.closed_at && (
            <span className="mono-note">closed {formatRelative(ticket.closed_at)}</span>
          )}
        </div>

        <ErrorNotice error={setStatus.error} />

        {/* The original report. Rendered in mono with whitespace preserved, because
            most of these bodies are pasted terminal output and reflowing it destroys
            the thing being reported. */}
        <Panel title="What you reported" index="01">
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <p
              style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                fontFamily: 'var(--mono)',
                fontSize: '0.875rem',
              }}
            >
              {ticket.body}
            </p>
            <AttachmentStrip attachments={ticket.attachments} />
          </div>
        </Panel>

        <Panel title="Conversation" index="02" padded={false}>
          {ticket.comments.length === 0 ? (
            <div className="empty">
              No replies yet. We answer in here, and email you when we do.
            </div>
          ) : (
            <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {ticket.comments.map((entry) => (
                <li
                  key={entry.id}
                  className="stack"
                  style={{
                    ['--gap' as string]: 'var(--s3)',
                    padding: 'var(--s4) var(--s5)',
                    borderTop: '1px solid var(--rule)',
                  }}
                >
                  <div className="row" style={{ gap: 'var(--s3)' }}>
                    {entry.author === 'staff' ? (
                      <Badge tone="signal">Archis</Badge>
                    ) : (
                      <Badge>you</Badge>
                    )}
                    <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{entry.author_name}</span>
                    <span className="mono-note" title={formatDateTime(entry.created_at)}>
                      {formatRelative(entry.created_at)}
                    </span>
                  </div>
                  <p
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                      fontSize: '0.9375rem',
                    }}
                  >
                    {entry.body}
                  </p>
                  <AttachmentStrip attachments={entry.attachments} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <form onSubmit={onReply}>
          <Panel
            title="Add to this ticket"
            index="03"
            footer={
              <div className="row row--between">
                <span className="mono-note">
                  {closed
                    ? 'Replying reopens this ticket — that is the point of a reply on a closed one.'
                    : 'We are notified as soon as you post.'}
                </span>
                <Button
                  type="submit"
                  variant="primary"
                  loading={comment.isPending}
                  disabled={body.trim().length === 0}
                >
                  {closed ? 'Reply and reopen' : 'Post reply'}
                </Button>
              </div>
            }
          >
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              {closed && (
                <Notice>
                  This ticket is closed. Anything you add reopens it and puts it back in front
                  of us — nothing is lost by replying instead of starting again.
                </Notice>
              )}

              <Field
                label="Reply"
                hint={`${(MAX_COMMENT - body.length).toLocaleString()} characters left.`}
              >
                <textarea
                  className="input"
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  maxLength={MAX_COMMENT}
                  rows={6}
                  style={{ resize: 'vertical', fontFamily: 'var(--mono)', fontSize: '0.875rem' }}
                  placeholder="What changed, what you tried, what it printed."
                />
              </Field>

              {attachmentsEnabled && (
                <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
                  <span className="field__label">Screenshots</span>
                  <AttachmentPicker
                    attachments={attachments}
                    onChange={setAttachments}
                    max={MAX_IMAGES}
                    maxBytes={maxBytes}
                    disabled={comment.isPending}
                  />
                </div>
              )}

              <ErrorNotice error={comment.error} />
            </div>
          </Panel>
        </form>
      </div>
    </AppShell>
  )
}
